import type {Instant, UUID} from './common'
// Types for roles and permissions API

export interface Permission {
  id: UUID
  resource: string
  action: string
  permissionString: string
}

/**
 * Issue 429 Part C — DTO returned by the paginated lookup endpoint
 * `GET /api/i/permissions`. Distinct from {@link Permission} (which is
 * used by the role-edit form's flat-list permissions endpoint) — the
 * lookup variant carries a derived `label` for direct rendering in
 * the LookupCombobox without re-concatenating resource and action.
 */
export interface AuthPermission {
  id: UUID
  resource: string
  action: string
  label: string
}

export interface Role {
  id: UUID
  /**
   * Stable kebab-case identifier (e.g. `iam-admin`) used to map upstream
   * OIDC role claims onto the role row. Read-only after create.
   */
  key: string
  name: string
  description: string | null
  systemRole: boolean
  permissions: Permission[]
  version: number
  createdAt: Instant
  updatedAt: Instant
}

export interface RoleCreateRequest {
  key: string
  name: string
  description?: string
  permissionIds?: UUID[]
}

export interface RoleUpdateRequest {
  name: string
  description?: string
  version: number
}

export interface RolePermissionsUpdateRequest {
  permissionIds: UUID[]
  version: number
}

/**
 * Response for a single role assignment, used by the service-account
 * roles card and the role assignments list page.
 */
export interface RoleAssignment {
  userId: UUID
  roleId: UUID
  roleName: string
  roleDescription: string | null
  assignedAt: Instant
  assignedById: UUID | null
  assignedByDisplayName: string | null
}

/**
 * Request body for `POST /api/i/auth/service-accounts/{id}/roles`.
 */
export interface AssignRoleRequest {
  roleId: UUID
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const PermissionResponseFields = {
    id: 'id',
    resource: 'resource',
    action: 'action',
    permissionString: 'permissionString',
} as const

export const RoleAssignmentResponseFields = {
    userId: 'userId',
    roleId: 'roleId',
    roleName: 'roleName',
    roleDescription: 'roleDescription',
    assignedAt: 'assignedAt',
    assignedById: 'assignedById',
    assignedByDisplayName: 'assignedByDisplayName',
} as const

export const RoleCreateRequestFields = {
    key: 'key',
    name: 'name',
    description: 'description',
    permissionIds: 'permissionIds',
} as const

export const RolePermissionsUpdateRequestFields = {
    permissionIds: 'permissionIds',
    version: 'version',
} as const

export const RoleResponseFields = {
    id: 'id',
    key: 'key',
    name: 'name',
    description: 'description',
    systemRole: 'systemRole',
    permissions: 'permissions',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const RoleUpdateRequestFields = {
    name: 'name',
    description: 'description',
    version: 'version',
} as const

export const UserWithRolesResponseFields = {
    id: 'id',
    name: 'name',
    email: 'email',
    active: 'active',
    type: 'type',
    roles: 'roles',
} as const
/* eslint-enable i18next/no-literal-string */
